import styled, { css } from 'styled-components'
import { X } from 'lucide-react'
import { colors, derivedColors, withScrollbarStyled } from '../../const'

export const Overlay = styled.div`
  background: rgba(26, 28, 33, 0.55);
  inset: 0;
  position: fixed;
  z-index: 1100;
`

export const Modal = styled.div`
  inset: 0;
  padding: 24px;
  position: fixed;
  z-index: 1101;

  @media screen and (max-width: 768px) {
    padding: 12px;
  }
`

export const Dialog = styled.div`
  background: ${colors.surface};
  border-radius: 16px;
  box-shadow:
    0 12px 32px rgba(26, 28, 33, 0.16),
    0 2px 8px rgba(26, 28, 33, 0.08);
  display: flex;
  flex-direction: column;
  height: min(820px, calc(100vh - 48px));
  margin: 0 auto;
  max-width: 960px;
  overflow: hidden;
  width: 100%;

  @media screen and (max-width: 768px) {
    height: calc(100vh - 24px);
  }
`

export const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors.borderSubtle};
  display: flex;
  justify-content: space-between;
  padding: 20px 24px 16px;

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const Title = styled.h2`
  color: ${colors.textPrimary};
  font-family: 'Inter';
  font-size: 1.375rem;
  font-weight: 800;
  margin: 0;
`

export const Subtitle = styled.p`
  color: ${colors.textSecondary};
  font-family: 'Inter';
  font-size: 0.9rem;
  margin: 0;
`

export const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 999px;
  color: ${colors.textSecondary};
  cursor: pointer;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  width: 36px;

  &:hover {
    background: ${derivedColors.subtleSurface};
    color: ${colors.textPrimary};
  }
`

export const CloseIcon = styled(X)`
  height: 20px;
  width: 20px;
`

export const Tabs = styled.div`
  border-bottom: 1px solid ${colors.borderSubtle};
  display: flex;
  padding: 0 24px;

  @media screen and (max-width: 768px) {
    padding: 0 16px;
  }
`

export const Tab = styled.button(
  ({ $active }) => css`
    background: transparent;
    border: none;
    border-bottom: 3px solid ${$active ? colors.primary : 'transparent'};
    color: ${$active ? colors.primary : colors.textSecondary};
    cursor: pointer;
    font-family: 'Inter';
    font-size: 0.92rem;
    font-weight: 700;
    padding: 14px 8px 11px;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.primary};
    }
  `
)

export const Body = styled.div`
  background: ${derivedColors.subtleSurface};
  flex: 1;
  overflow: auto;
  padding: 24px;

  ${withScrollbarStyled};

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

export const LabelPanel = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 100%;
`

export const LabelImage = styled.img`
  background: ${colors.surface};
  border-radius: 12px;
  box-shadow:
    0 1px 2px rgba(26, 28, 33, 0.08),
    0 10px 24px rgba(26, 28, 33, 0.1);
  display: block;
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`

export const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const HeroCard = styled.section`
  align-items: center;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(37, 99, 235, 0.04));
  border: 1px solid rgba(37, 99, 235, 0.12);
  border-radius: 16px;
  display: grid;
  gap: 16px;
  grid-template-columns: auto 1fr;
  padding: 20px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const HeroLogo = styled.img`
  height: 64px;
  object-fit: contain;
  width: 64px;
`

export const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const HeroName = styled.h3`
  color: ${colors.textPrimary};
  font-family: 'Inter';
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0;
`

export const HeroDescription = styled.p`
  color: ${colors.textSecondary};
  font-family: 'Inter';
  font-size: 0.94rem;
  line-height: 1.45;
  margin: 0;
`

export const SectionsGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const SectionCard = styled.section`
  background: ${colors.surface};
  border: 1px solid ${colors.borderSubtle};
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
`

export const SectionTitle = styled.h4`
  color: ${colors.primary};
  font-family: 'Inter';
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0;
`

export const FieldList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
`

export const FieldRow = styled.div`
  display: grid;
  gap: 4px;
`

export const FieldLabel = styled.dt`
  color: ${colors.textSecondary};
  font-family: 'Inter';
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin: 0;
  text-transform: uppercase;
`

export const FieldValue = styled.dd`
  color: ${colors.textPrimary};
  font-family: 'Inter';
  font-size: 0.92rem;
  line-height: 1.45;
  margin: 0;
`

export const FieldLink = styled.a`
  color: ${colors.primary};
  font-family: 'Inter';
  font-size: 0.92rem;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const WarningCard = styled.section`
  background: rgba(255, 193, 7, 0.16);
  border: 1px solid rgba(255, 193, 7, 0.32);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
`

export const WarningTitle = styled.h4`
  color: ${colors.textPrimary};
  font-family: 'Inter';
  font-size: 0.95rem;
  font-weight: 800;
  margin: 0;
`

export const WarningList = styled.ul`
  color: ${colors.textPrimary};
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding-left: 18px;
`

export const WarningItem = styled.li`
  font-family: 'Inter';
  font-size: 0.92rem;
  line-height: 1.45;
`
